import type { DeepPartial } from "simplytyped";
import { z } from "zod";

const UserZod = z.object({
  createdAt: z.string(), // "2017-04-30T12:17:01.276+00:00"
  email: z.string(), // "john.smith@example.com"
  firstName: z.string(), // "John"
  id: z.string(), // "e59c136f-d52d-4a92-923b-7c6f20106dc8"
  lastName: z.string(), // "Smith"
  phone: z.string().nullish(), // "+61491570156"
  updatedAt: z.string(), // "2017-04-30T12:17:01.276+00:00"
  username: z.string(), // "john.smith"
  vendorId: z.string(), // "MessageMedia"
});

export const MM_PAYMENT_TYPE = z.enum(["POSTPAID", "PREPAID", "PREPAID_MONEY"]);
export type MmPaymentType = z.infer<typeof MM_PAYMENT_TYPE>;

const AccountZod = z.object({
  billingType: z.string(), // "BILLED",
  businessId: z.string().nullable().optional(),
  company: z.string().nullable(), // "MessageMedia"
  country: z.string().length(2), // "AU"
  createdAt: z.string(), // "2017-04-30T12:17:01.276+00:00"
  features: z.array(z.string()), // (16) features as strings
  id: z.string(), // "parent-0"
  immigrated: z.boolean(), // true
  originalCreatedAt: z.string().optional(), // "2017-04-30T12:17:01.276+00:00"
  parentId: z.string().optional(), // "MessageMedia"
  paymentType: MM_PAYMENT_TYPE, // "POSTPAID"
  primaryUserEmailVerified: z.boolean(), // true
  timezone: z.string(), // "Australia/Melbourne"
  updatedAt: z.string(), // "2017-04-30T12:17:01.276+00:00"
  vendorId: z.string(), // "MessageMedia"
  verificationStatus: z.string(), // "VERIFIED"
});

export const MmUserZod = z.object({
  account: AccountZod,
  accountId: z.string(), // 'parent-0'
  defaultCountryCode: z.string().length(2), // 'AU'
  featureSet: z.object({
    id: z.string(), // 'f9f0587b-dead-d586-47cb-bcfa894c8c20'
    features: z.array(z.string()), // Array of unknown features
    createdAt: z.string(), // '2017-04-30T12:17:01.276Z'
  }),
  featureSetId: z.string(), // 'f9f0587b-dead-d586-47cb-bcfa894c8c20'
  roles: z.array(z.string()), // ['admin', 'billing', 'user']
  segmentUserId: z.string(), // 'mock-api-segment-user-id'
  user: UserZod,
  userId: z.string(), // 'e59c136f-d52d-4a92-923b-7c6f20106dc8,
  vendorId: z.string(), // 'MessageMedia'
});

export type MmUser = z.infer<typeof MmUserZod>;
export type MmUserPartial = Omit<
  DeepPartial<MmUser>,
  "userId" | "user" | "featureSet" | "accountId"
> &
  Pick<MmUser, "userId" | "accountId"> & {
    user: Omit<
      DeepPartial<MmUser["user"]>,
      "email" | "firstName" | "lastName" | "username"
    > &
      Pick<MmUser["user"], "email" | "firstName" | "lastName" | "username">;
    featureSet: Omit<DeepPartial<MmUser["featureSet"]>, "features"> &
      Pick<MmUser["featureSet"], "features">;
  };
